import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { billing } from '@repletoryreach/sdk-api';
import { Stack, Group, Text, Button, Badge } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { formatDateTime, formatCount } from '../../../lib/format';
import { useCredits } from '../../../lib/credits';
import { useT } from '../../../i18n/I18nProvider';
import { LedgerEntryExplain } from './LedgerEntryExplain';
import { sourceKindLabel } from './usage-labels';

export interface UsageEntriesTarget {
  kind: billing.UsageSourceKind | 'sku';
  id: string;
  label: string;
}

const PAGE = 50;

/**
 * The ledger entries behind one figure: a broadcast, a contact, a flow, a SKU.
 * Every row opens the same explanation the ledger page shows, so "why was I
 * charged this" has one answer wherever it is asked.
 */
export function UsageEntriesDrawer({ target, currency, onClose }: {
  target: UsageEntriesTarget | null;
  currency: string;
  onClose: () => void;
}) {
  const t = useT();
  const credits = useCredits();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<billing.LedgerEntry[]>([]);
  const [explain, setExplain] = useState<billing.LedgerEntry | null>(null);

  const q = useQuery({
    queryKey: ['usage', 'entries', target?.kind, target?.id, cursor],
    enabled: !!target,
    queryFn: async () => {
      const page = await billing.listUsageEntries(target!.kind, target!.id, { limit: PAGE, cursor });
      setRows((prev) => (cursor ? [...prev, ...page.data] : page.data));
      return page;
    }
  });

  const meta = q.data?.meta;
  const close = () => { setCursor(undefined); setRows([]); setExplain(null); onClose(); };

  return (
    <Drawer opened={!!target} onClose={close} title={target?.label ?? ''} position="right" size="lg">
      <Stack gap="sm">
        {meta && (
          <Group justify="space-between">
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.usage.drawer.summary', { count: formatCount(meta.total) })}</Text>
            <Text role="bodySmall" fw={600}>{credits(meta.spent_minor, currency)}</Text>
          </Group>
        )}
        {q.error && <Alert color="error" role="alert">{t('billing.ledgerError')}</Alert>}
        {!q.isPending && rows.length === 0 && <EmptyState title={t('billing.noTransactionsYet')} />}
        {rows.map((e) => (
          <button key={e.id} type="button" className="rr-billing__entry" onClick={() => setExplain(e)}>
            <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
              <Text role="bodySmall" truncate>{e.description ?? e.sku_code ?? e.entry_type}</Text>
              <Group gap={6}>
                <Badge size="xs" variant="light">{sourceKindLabel(t, e.source_kind ?? e.entry_type)}</Badge>
                <Text role="caption" c="var(--rr-color-text-muted)">{formatDateTime(e.occurred_at)}</Text>
              </Group>
            </Stack>
            <Text role="bodySmall" fw={600} className={`rr-billing__amount rr-billing__amount--${e.amount_minor < 0 ? 'debit' : 'credit'}`}>
              {e.amount_minor < 0 ? '−' : '+'}{credits(Math.abs(e.amount_minor), e.currency ?? currency)}
            </Text>
          </button>
        ))}
        {meta?.next_cursor && (
          <Button size="xs" variant="default" loading={q.isFetching} onClick={() => setCursor(meta.next_cursor!)}>
            {t('billing.usage.drawer.more')}
          </Button>
        )}
      </Stack>
      <LedgerEntryExplain entry={explain} onClose={() => setExplain(null)} />
    </Drawer>
  );
}
